import {AfterViewInit, Component, Inject, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {AppService} from "../../app.service";
import {ApiRequestService} from "../api-request.service";
import {UtilsService} from "../utils.service";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {tap} from "rxjs/operators";
import {ApiDataSource} from "../../utils/api-data-source";
import {
  IncidentsSelectorFilterComponent
} from "../incidents-selector-filter/incidents-selector-filter.component";

@Component({
  selector: 'app-incidents-selector',
  templateUrl: './incidents-selector.component.html',
  styleUrls: ['./incidents-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class IncidentsSelectorComponent implements OnInit, AfterViewInit {

  // columns to show and the data source
  displayedColumns: string[] = [
    'select',
    'id',
    'details',
    'type',
    'status',
    'site',
    'date_incident'
  ];
  dataSource: IncidentsSelectorDataSource;

  // the paginator and sorter
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    public dialogRef: MatDialogRef<IncidentsSelectorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    // Set multiple to false if it is not present.
    if (typeof this.data['multiple'] === 'undefined') {
      this.data['multiple'] = false;
    }

    // Set required to false if it is not present.
    if (typeof this.data['required'] === 'undefined') {
      this.data['required'] = false;
    }

    // Set hide_assets to false if it is not present.
    if (typeof this.data['hide_assets'] === 'undefined') {
      this.data['hide_assets'] = false;
    }

    // Construct the data source.
    this.dataSource = new IncidentsSelectorDataSource(this.app, this.api, this.data['multiple']);

    // Apply the Asset visibility filter.
    this.dataSource.hide_assets = this.data['hide_assets'];

    // Default selection to nothing when no selected values are provided.
    if (typeof this.data['selected'] === 'undefined') {
      this.data['selected'] = [];
    }

    // Preselect the provided values.
    for (let i = 0; i < this.data['selected'].length; i++) {
      if (this.data['selected'][i]) {
        this.dataSource.selection.select(this.data['selected'][i]);
      }
    }

    // Scope the list to a specific site if provided.
    if (this.data['site_id']) {
      this.dataSource.site_ids = [this.data['site_id']];
    }
  }

  ngOnInit() {
    this.dataSource.getData();
  }

  ngAfterViewInit() {
    // Reset the paginator when sorting.
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.order_by = this.sort.active;
      this.dataSource.order = this.sort.direction;

      this.dataSource.getData(false);
    });

    // Subscribe to the paginator and sorter tap events.
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  /**
   * Clear the current selection.
   *
   * @return {void}
   */
  onClearSelected(): void {
    this.dataSource.selection.clear();
  }

  /**
   * Open the filter dialog and apply the selected filters.
   *
   * @return {void}
   */
  onFilter(): void {
    this.utils.showComponentDialog(
      IncidentsSelectorFilterComponent,
      {
        type: this.dataSource.type,
        status: this.dataSource.status,
        site_ids: this.dataSource.site_ids,
        user_ids: this.dataSource.user_ids,
        asset_ids: this.dataSource.asset_ids,
        date_range: this.dataSource.date_range,
        hide_assets: this.dataSource.hide_assets
      },
      {
        width: '900px'
      },
      (results) => {
        if (typeof results === 'undefined') { return; }

        this.dataSource.type = results.type ?? '';
        this.dataSource.status = results.status ?? '';
        this.dataSource.site_ids = results.site_ids ?? [];
        this.dataSource.user_ids = results.user_ids ?? [];
        this.dataSource.asset_ids = results.asset_ids ?? [];
        this.dataSource.date_range = results.date_range ?? [];

        this.dataSource.getData(true);
      }
    );
  }

  /**
   * Save the selected data and close the dialog.
   *
   * @return {void}
   */
  onSaveAndClose(): void {
    let response: number | number[];

    if (this.data['multiple']) {
      response = this.dataSource.selection.selected;
    } else {
      response = this.dataSource.selection.selected.length ? this.dataSource.selection.selected[0] : 0;
    }

    this.dialogRef.close(response);
  }

  /**
   * Close the dialog.
   *
   * @return {void}
   */
  onClose(): void {
    this.dialogRef.close();
  }
}

export class IncidentsSelectorDataSource extends ApiDataSource {
  order_by = 'date_incident';
  order = 'desc';
  site_ids: number[] = [];

  // Filter options (mirrors the main Incidents list).
  type: string = '';
  status: string = '';
  user_ids: number[] = [];
  asset_ids: number[] = [];
  date_range: Date[] = [];
  hide_assets: boolean = false;

  getData(resetOffset: boolean = false) {
    this.makeRequest('v2/incidents', resetOffset, {
      site_ids: this.site_ids.length ? this.site_ids.join(',') : '',
      type: this.type || '',
      status: this.status || '',
      user_ids: this.user_ids.length ? this.user_ids.join(',') : '',
      asset_ids: this.asset_ids.length ? this.asset_ids.join(',') : '',
      date_range: this.date_range && this.date_range.length > 1 ? [
        this.date_range[0].getTime() / 1000,
        this.date_range[1].getTime() / 1000
      ].join(',') : ''
    });
  }
}
